import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { HTTP_STATUSES, SERVER_INTERNAL_ERRORS } from '../consts/apiCodes';
import {
  getLinkedInTalent, getTalent, postTalent, getIcons, updateContactDetails,
  requestMissingContactDetails, getMissingContactDetails,
} from '../api/talentsApi';
import { ContactDetail } from '../components/ContactDetailsModal';

// todo MATAN1: change result type

type GetTalentResult = {
  isLoading: boolean,
  isFetching: boolean,
  data: FormattedTalentResponse,
  refetch: () => void,
}

export const useGetTalent = (
  talentId:string,
  onSuccessCallback?:
  ((data: FormattedTalentResponse)=> void) | null,
  disabled = false,
  queryKey = '',
): GetTalentResult => {
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<FormattedTalentResponse>(['talent',
    talentId,
    queryKey,
  ], () => getTalent(talentId), {
    enabled: !disabled,
    onSuccess: (talent) => {
      if (onSuccessCallback) {
        onSuccessCallback(talent);
      }
    },
  });

  return {
    isLoading,
    isFetching,
    refetch,
    data: {
      talentId: data?.talentId || '',
      version: data?.version || '',
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      profileImage: data?.profileImage || '',
      phoneNumbers: data?.phoneNumbers || [],
      emails: data?.emails || [],
      location: data?.location || {
        city: '',
        state: '',
        country: '',
      },
      employmentHistory: data?.employmentHistory || [],
      educationHistory: data?.educationHistory || [],
      attributes: data?.attributes || [],
      socialLinks: data?.socialLinks || [],
      webResults: data?.webResults || [],
      technicalSkills: data?.technicalSkills || [],
      createdManually: data?.createdManually || false,
      createdBy: data?.createdBy || '',
      createdAt: data?.createdAt || '',
      updatedAt: data?.updatedAt || '',
      updatedBy: data?.updatedBy || '',
      linkedinUrl: data?.linkedinUrl || '',
    },
  };
};

export const useGetIcons = (): { data: Icon[] } => {
  const {
    data,
  } = useQuery<Icon[]>(['icons'], () => getIcons());

  if (data) return { data };

  return { data: [] };
};

export const useGetLinkedIn = (
  successCallback: (data: FormattedTalentResponse) => void,
  onPrivateProfileErrorCallback: () => void,
  onErrorCallback: (message: string) => void,
): UseMutationResult<FormattedTalentResponse, AxiosError, string, unknown> => {
  return useMutation((linkedInUrl: string) => getLinkedInTalent(linkedInUrl), {
    onSuccess: (data) => {
      successCallback(data);
    },
    onError: (error: AxiosError) => {
      const {
        statusCode,
        internalErrorCode,
        fieldsErrors,
        message,
      } = error?.response?.data || {};
      if (
        error &&
        statusCode === HTTP_STATUSES.NOT_FOUND &&
        internalErrorCode === SERVER_INTERNAL_ERRORS.LINKEDIN_PROFILE_DOES_NOT_EXIST) {
        onPrivateProfileErrorCallback();

        return;
      }

      let errorMessage = '';

      if (fieldsErrors && fieldsErrors.length > 0) {
        // show error from fields property in server response
        // eslint-disable-next-line prefer-destructuring
        errorMessage = fieldsErrors[0];
      } else if (message) {
        // show message from server response
        errorMessage = message;
      } else if (error.message) {
        // show default axios error message
        errorMessage = error.message;
      }

      onErrorCallback(errorMessage);
    },
  });
};

export const usePostTalent = (
  onSuccessCallback:() => void,
): UseMutationResult<TalentResponse, AxiosError, TalentUpdate, unknown>  => {
  return useMutation((savedObject:TalentUpdate) => {
    return postTalent(savedObject);
  }, {
    onSuccess: () => {
      onSuccessCallback();
    },
  },
  );
};

export const useUpdateContactDetails = (
  onSuccessCallback:(data: ContactDetails) => void,
  talentId: string,
// todo: remove eslint-disable
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  return useMutation((savedObject:ContactDetail) => {
    const saveData: ContactDetails = {
      phoneNumbers: savedObject.phones.map((phone: PhoneItem) => phone.value),
      emails: savedObject.emails.map((email: EmailItem) => email.value),
    };

    return updateContactDetails(saveData, talentId);
  }, {
    onSuccess: (data:ContactDetails) => {
      onSuccessCallback({
        emails: data.emails,
        phoneNumbers: data.phoneNumbers,
      });
    },
  },
  );
};

export const useRequestMissingContactDetails = (
  onSuccessCallback: () => void,
): UseMutationResult<void, AxiosError, string> => {
  return useMutation((talentId: string) => {
    return requestMissingContactDetails(talentId);
  }, {
    onSuccess: onSuccessCallback,
  });
};

type GetMissingContactDetails = {
  data?: MissingContactDetails,
  isLoading: boolean,
  error: AxiosError | null,
  isError: boolean,
  refetch: () => void,
}
export const useGetMissingContactDetailsStatus = (
  onSuccessCallback: (contactDetails: MissingContactDetails) => void,
  talentId: string,
  isEnabled: boolean,
): GetMissingContactDetails => {
  const {
    data, error, isLoading, isError, refetch,
  } = useQuery<MissingContactDetails, AxiosError | null>(
    ['missing-contact-details', talentId], () => {
      return getMissingContactDetails(talentId);
    }, {
      enabled: isEnabled,
      refetchInterval: 15000,
      retry: false,
      onSuccess: (contactDetails: MissingContactDetails) => {
        onSuccessCallback(contactDetails);
      },
    });

  return {
    data,
    error,
    isLoading,
    isError,
    refetch,
  };
};
